import closestTo from 'date-fns/closestTo';
import compareAsc from 'date-fns/compareAsc';
import parse from 'date-fns/parse';
import memoize from 'lodash.memoize';
import {
  GetSortedHostsType,
  SortableIAccount,
} from 'modules/experience/providers/SortedHostProvider';
import gen from 'random-seed';
import { compareTimeStrings } from 'utils/TimeHelper';
import formatDate from 'utils/formatDate';

const randomSeedSorter = (a: SortableIAccount, b: SortableIAccount) => {
  return a.sortValue - b.sortValue;
};

const compareValues = (a, b) => (a === b ? 0 : !a ? 1 : -1);
const compareTimes = (a, b) =>
  !a || !b ? compareValues(a, b) : compareTimeStrings(a, b);

const firstAvailableDateSorter = (a: SortableIAccount, b: SortableIAccount) => {
  if (a.firstAvailableTime && b.firstAvailableTime) {
    const timeA = a.firstAvailableTime;
    const timeB = b.firstAvailableTime;
    return compareTimes(timeA, timeB);
  }
  const dateA = a.firstAvailableDate
    ? parse(a.firstAvailableDate, 'yyyy-MM-dd', new Date())
    : 0;
  const dateB = b.firstAvailableDate
    ? parse(b.firstAvailableDate, 'yyyy-MM-dd', new Date())
    : 0;
  return compareAsc(dateA, dateB);
};

type AssigningSortValueParams = {
  hosts: SortableIAccount[];
  experienceId: string;
  availableDates?: { [id: string]: string[] };
  // Matches the structure used elsewhere in this file where we read `.data`
  availableTimes?: { [date: string]: { data: { [hostId: string]: string[] } } };
  selectedDate?: string;
};

export const assigningSortValue = ({
  hosts,
  experienceId,
  selectedDate,
  availableDates,
  availableTimes,
}: AssigningSortValueParams): SortableIAccount[] => {
  const seed = `${new Date().toDateString()} ${experienceId}`;
  const generator = gen.create(seed);

  const parsedSelectedDate = selectedDate
    ? parse(selectedDate, 'yyyy-MM-dd', new Date())
    : new Date();
  const selectedOrToday = (selectedDate ||
    formatDate(new Date(), 'yyyy-MM-dd')) as string;
  const timesOnDate: { [hostId: string]: string[] } =
    availableTimes?.[selectedOrToday]?.data ?? {};

  return hosts.map((host) => {
    const hostDatesList = availableDates?.[host.id] || [];
    // Filter out falsy entries to avoid nulls in the array for date-fns
    const hostDates = hostDatesList
      .filter(Boolean)
      .map((date) => parse(date as string, 'yyyy-MM-dd', new Date()));
    const hostTimes = timesOnDate?.[host.id] ?? [];

    const firstAvailableDate = hostDates.length
      ? closestTo(parsedSelectedDate, hostDates)
      : undefined;
    return {
      ...host,
      firstAvailableTime: hostTimes[0],
      firstAvailableDate: firstAvailableDate
        ? (formatDate(firstAvailableDate, 'yyyy-MM-dd') ?? undefined)
        : undefined,
      sortValue: generator.random(0, Number.MAX_SAFE_INTEGER),
    };
  });
};

type GroupByAvailabilityParams = {
  hosts: SortableIAccount[];
  date: string;
  availableTimes: { [key: string]: { data: { [key: string]: string[] } } };
  time: string;
};

export const groupByAvailability = ({
  hosts,
  availableTimes,
  date,
  time,
}: GroupByAvailabilityParams): {
  available: SortableIAccount[];
  unavailable: SortableIAccount[];
} => {
  const timesOnDate = availableTimes?.[date]?.data ?? {};
  return hosts.reduce<{
    available: SortableIAccount[];
    unavailable: SortableIAccount[];
  }>(
    (accumulator, host) => {
      const hostTimes = timesOnDate?.[host.id] ?? [];

      if (hostTimes.includes(time) || (!time && hostTimes.length > 0)) {
        accumulator.available.push(host);
      } else {
        accumulator.unavailable.push(host);
      }
      return accumulator;
    },
    { available: [], unavailable: [] },
  );
};

export const groupByLanguage = ({
  hosts,
  language,
}: {
  hosts: SortableIAccount[];
  language: string;
}): { same: SortableIAccount[]; other: SortableIAccount[] } => {
  return hosts.reduce<{
    same: SortableIAccount[];
    other: SortableIAccount[];
  }>(
    (accumulator, host) => {
      if (host.languages?.some((l) => l.language_code === language)) {
        accumulator.same.push(host);
      } else {
        accumulator.other.push(host);
      }
      return accumulator;
    },
    { same: [], other: [] },
  );
};

export const getSortedHosts = memoize(
  ({
    experienceId,
    hosts,
    date,
    time,
    availableTimes,
    availableDates,
    language,
    highlightedHost,
    sortByAvailability,
  }: GetSortedHostsType): SortableIAccount[] => {
    if (!hosts) {
      return [];
    }

    const sortedHosts = (() => {
      const hostsWithSortValue = assigningSortValue({
        hosts: hosts as SortableIAccount[],
        experienceId,
        availableDates,
        availableTimes,
        selectedDate: date,
      });
      const hasAvailabilityInfo = (date || time) && availableTimes;
      const unavailableHostsSorter = sortByAvailability
        ? firstAvailableDateSorter
        : randomSeedSorter;

      if (!hasAvailabilityInfo && !language) {
        return hostsWithSortValue.sort(
          sortByAvailability ? firstAvailableDateSorter : randomSeedSorter,
        );
      }

      if (hasAvailabilityInfo && language) {
        const hostsGroupedByAvailabilityHosts = groupByAvailability({
          availableTimes,
          time: time || '',
          date: date || '',
          hosts: hostsWithSortValue,
        });
        const availableHostsGroupedByLanguage = groupByLanguage({
          language,
          hosts: hostsGroupedByAvailabilityHosts.available,
        });
        return [
          ...availableHostsGroupedByLanguage.same.sort(randomSeedSorter),
          ...availableHostsGroupedByLanguage.other.sort(randomSeedSorter),
          ...hostsGroupedByAvailabilityHosts.unavailable.sort(
            unavailableHostsSorter,
          ),
        ];
      }

      if (!hasAvailabilityInfo && language) {
        const availableHostsGroupedByLanguage = groupByLanguage({
          language,
          hosts: hostsWithSortValue,
        });
        return [
          ...availableHostsGroupedByLanguage.same.sort(
            sortByAvailability ? firstAvailableDateSorter : randomSeedSorter,
          ),
          ...availableHostsGroupedByLanguage.other.sort(
            sortByAvailability ? firstAvailableDateSorter : randomSeedSorter,
          ),
        ];
      }

      if (hasAvailabilityInfo && !language) {
        const hostsGroupedByAvailabilityHosts = groupByAvailability({
          availableTimes,
          time: time || '',
          date: date || '',
          hosts: hostsWithSortValue,
        });
        return [
          ...hostsGroupedByAvailabilityHosts.available.sort(randomSeedSorter),
          ...hostsGroupedByAvailabilityHosts.unavailable.sort(
            unavailableHostsSorter,
          ),
        ];
      }

      return []; // this never happens but needed for lint because it thinks not all code paths return a value
    })();

    if (highlightedHost) {
      return sortedHosts.sort((current, next) =>
        current.id === highlightedHost
          ? -1
          : next.id === highlightedHost
            ? 1
            : 0,
      );
    }

    return sortedHosts;
  },
);
